import {Box, Typography} from '@mui/material';

import {t} from 'i18next';
import {
  forwardRef,
  ForwardRefExoticComponent,
  Fragment,
  RefAttributes,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import {useDispatch} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {useToast} from '../../../../contexts/toastContext/ToastContext';
import useFCAgentConnection from '../../../../hooks/fc-upload-evidence-source/useFCAgentConnection';
import useFCLocalFiles from '../../../../hooks/fc-upload-evidence-source/useFCLocalFiles';
import useFCUploadFiles from '../../../../hooks/fc-upload-evidence-source/useFCUploadFiles';
import {setEvidenceSourceDetails} from '../../../../redux/slices/uploadEvidenceSourceSlice';
import {FileCatalystModalType} from '../../../../types/upload-new-eviidence/evidenceSourceTypes';
import {FCFileGridItem} from '../../../../types/upload-new-eviidence/fcUploadFileTypes';
import {FCFileTransferState} from '../../../../utils/fcUploadFileUtils';
import {convertFileSize, isEmptyInstance} from '../../../../utils/utils';
import AurButton from '../../../common/aur-button/AurButton';
import AurLoadingIndicator from '../../../common/aur-loading-indicator/AurLoadingIndictor';
import {EvidenceSourceInfo} from './components/EvidenceSourceInfo';
import FCLocalFileList from './components/FCLocalFileList';
import FCUploadDataGrid from './components/FCUploadDataGrid';
import {useFCUploadFileGridConfig} from './components/fcUploadFileGridConfig';
import {FileCatalystPopUpModal} from './components/FileCatalystPopUpModal';
import {FileCatalystUnavailableScreen} from './FileCatalystUnavailableScreen';
import {setIndexingBannerData} from '../../../../redux/slices/indexingManagementSlice';
import FCRetryConnectionView from './components/FCRetryConnectionView';
import {useAnalytics} from '../../../../hooks/useAnalytics';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_REGIONS,
  GA_SUBPAGE_TYPE,
} from '../../../../constants/analyticsConstants';

export type UploadHighSpeedTabHandler = {
  cancelPendingUpload: () => void;
};

interface UploadHighSpeedTabProps {
  isActiveTab: boolean;
  onChangeIsFCAgentRunning?: (isRunning: boolean) => void;
  onChangeUploadFileCount?: (fileCount: number) => void;
}

const UploadHighSpeedTab: ForwardRefExoticComponent<
  UploadHighSpeedTabProps & RefAttributes<UploadHighSpeedTabHandler>
> = forwardRef<UploadHighSpeedTabHandler, UploadHighSpeedTabProps>(
  ({isActiveTab, onChangeIsFCAgentRunning, onChangeUploadFileCount}, ref) => {
    const [isFCAgentLaunched, setIsFCAgentLaunched] = useState<boolean>(false);
    const {trackGAEvent} = useAnalytics();

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const {showToast} = useToast();

    const {
      isAgentRunning = false,
      isConnectionFailed,
      connectionKey,
      cancelFCHeartBeat,
      fcEncryptedPayload,
      launchFCAgent,
      getHeartBeat,
    } = useFCAgentConnection(isActiveTab, showToast);

    const {
      currentDirectory,
      setCurrentDirectory,
      fetchFilesForCurrentDirectory,
      directoryFiles,
      isLocalFilesLoading,
    } = useFCLocalFiles(isAgentRunning, connectionKey, showToast);

    const {
      pendingFileArray = [],
      uploadedFileArray = [],
      canManageEvidence,
      handleDeleteRemoteFile,
      handleCancelUploadFileAction,
      handleRetryFailedUploadFileAction,
      handleUploadLocalFile,
      isIntakeLoading,
      handleInitiateUpload,
      stopStatusPolling,
      cancelAllPendingUpload,
    } = useFCUploadFiles(fcEncryptedPayload, connectionKey, showToast);

    const isAgentStartedConnecting = useMemo(
      () =>
        !isAgentRunning &&
        !isConnectionFailed &&
        isEmptyInstance(connectionKey),
      [connectionKey, isAgentRunning, isConnectionFailed]
    );

    const onClickPendingFileActionButton = useCallback(
      (row: FCFileGridItem) => {
        trackGAEvent({
          eventName: GA_EVENT_NAMES.CLICK,
          click: {
            name:
              row?.status === FCFileTransferState.FAILED
                ? t('gaTracking.uploadEvidence.retryUpload')
                : t('gaTracking.uploadEvidence.cancelUpload'),
            type: GA_CLICK_TYPES.LINK,
            region: GA_REGIONS.TAB,
            action:
              row?.status === FCFileTransferState.FAILED
                ? t('gaTracking.uploadEvidence.uploadFileRetryUploading')
                : t('gaTracking.uploadEvidence.uploadFileCancelUploading'),
          },
          files: {
            file_size_mb: row
              ? convertFileSize(row.size, 'Bytes', 'MB').toString()
              : '0',
          },
          subPage: {
            subpage_name: t('gaTracking.uploadEvidence.highSpeedUpload'),
            subpage_type: GA_SUBPAGE_TYPE.TAB,
          },
        });
        if (row?.status === FCFileTransferState.FAILED) {
          handleRetryFailedUploadFileAction(row);
        } else {
          setFileActionModal({
            type: FileCatalystModalType.CancelUpload,
            fileGridItem: {...row},
          });
        }
      },
      [handleRetryFailedUploadFileAction]
    );

    const onClickUploadedFileActionButton = useCallback(
      (row: FCFileGridItem) => {
        if (!isEmptyInstance(row)) {
          setFileActionModal({
            type: FileCatalystModalType.Remove,
            fileGridItem: {...row},
          });

          trackGAEvent({
            eventName: GA_EVENT_NAMES.CLICK,
            click: {
              name: t('gaTracking.uploadEvidence.removeFile'),
              type: GA_CLICK_TYPES.LINK,
              region: GA_REGIONS.TAB,
              action: t('gaTracking.uploadEvidence.uploadFileRemoveUploading'),
            },
            files: {
              file_size_mb: row
                ? convertFileSize(row.size, 'Bytes', 'MB').toString()
                : '0',
            },
            subPage: {
              subpage_name: t('gaTracking.uploadEvidence.highSpeedUpload'),
              subpage_type: GA_SUBPAGE_TYPE.TAB,
            },
          });
        }
      },
      []
    );

    const {pendingFileGridConfig, uploadedFileGridConfig} =
      useFCUploadFileGridConfig(
        onClickPendingFileActionButton,
        onClickUploadedFileActionButton
      );

    useImperativeHandle(ref, () => ({
      cancelPendingUpload: () => {
        stopStatusPolling();
        cancelAllPendingUpload();
        cancelFCHeartBeat();
      },
    }));

    useEffect(() => {
      dispatch(
        setIndexingBannerData({
          message: '',
          showIndexBanner: isActiveTab && !isAgentRunning,
        })
      );
    }, [dispatch, isActiveTab, isAgentRunning]);

    useEffect(() => {
      isActiveTab &&
        isAgentRunning &&
        connectionKey &&
        !isFCAgentLaunched &&
        launchFCAgent();
    }, [
      connectionKey,
      isActiveTab,
      isAgentRunning,
      isFCAgentLaunched,
      launchFCAgent,
    ]);

    useEffect(() => {
      isActiveTab && !isFCAgentLaunched && setIsFCAgentLaunched(true);
    }, [isActiveTab, isFCAgentLaunched]);

    useEffect(() => {
      isActiveTab &&
        !isAgentRunning &&
        isEmptyInstance(connectionKey) &&
        launchFCAgent();
    }, [connectionKey, isActiveTab, isAgentRunning, launchFCAgent]);

    useEffect(() => {
      onChangeUploadFileCount?.(
        pendingFileArray.length + uploadedFileArray.length
      );
    }, [
      onChangeUploadFileCount,
      pendingFileArray.length,
      uploadedFileArray.length,
    ]);

    useEffect(() => {
      onChangeIsFCAgentRunning?.(isAgentRunning);
    }, [onChangeIsFCAgentRunning, isAgentRunning]);

    const [fileActionModal, setFileActionModal] = useState<{
      type: FileCatalystModalType | null;
      fileGridItem: FCFileGridItem | null;
    }>({
      type: null,
      fileGridItem: null,
    });

    const onCancelDialog = useCallback(
      () => setFileActionModal({type: null, fileGridItem: null}),
      []
    );

    const onConfirmYesDialog = useCallback(() => {
      if (!isEmptyInstance(fileActionModal?.fileGridItem)) {
        const {type, fileGridItem} = fileActionModal;
        switch (type) {
          case FileCatalystModalType.CancelUpload: {
            handleCancelUploadFileAction({...fileGridItem});
            break;
          }
          case FileCatalystModalType.Remove: {
            handleDeleteRemoteFile({...fileGridItem});
            break;
          }
        }
      }
      setFileActionModal({type: null, fileGridItem: null});
    }, [fileActionModal, handleDeleteRemoteFile, handleCancelUploadFileAction]);

    const handleInitiateUploadClick = useCallback(async () => {
      trackGAEvent({
        eventName: GA_EVENT_NAMES.CLICK,
        click: {
          name: t('gaTracking.uploadEvidence.initiateIntake'),
          type: GA_CLICK_TYPES.LINK,
          region: GA_REGIONS.TAB,
          action: t('gaTracking.uploadEvidence.uploadFileComplete'),
        },
        files: {
          file_size_mb: convertFileSize(
            uploadedFileArray.reduce((sum, item) => sum + item.size, 0),
            'Bytes',
            'MB'
          ).toString(),
          file_count: `${uploadedFileArray.length}`,
          success_rate: `${((uploadedFileArray.length * 100) / (uploadedFileArray.length + pendingFileArray.length)).toFixed(2)}`,
        },
        subPage: {
          subpage_name: t('gaTracking.uploadEvidence.highSpeedUpload'),
          subpage_type: GA_SUBPAGE_TYPE.TAB,
        },
      });
      const data = await handleInitiateUpload();
      if (!data) return;

      dispatch(setEvidenceSourceDetails(undefined));
      localStorage?.setItem('currentPath', '');
      if (canManageEvidence) navigate('/manage-evidence');
      else navigate('/welcome-single-client');
    }, [canManageEvidence, dispatch, handleInitiateUpload, navigate]);

    const renderLocalFileListContainer = useCallback(() => {
      return (
        <FCLocalFileList
          isActiveTab={isActiveTab}
          handleUploadLocalFile={handleUploadLocalFile}
          currentDirectory={currentDirectory}
          setCurrentDirectory={setCurrentDirectory}
          fetchFilesForCurrentDirectory={fetchFilesForCurrentDirectory}
          directoryFiles={directoryFiles}
          isLocalFilesLoading={isLocalFilesLoading}
        />
      );
    }, [
      isActiveTab,
      handleUploadLocalFile,
      currentDirectory,
      setCurrentDirectory,
      fetchFilesForCurrentDirectory,
      directoryFiles,
      isLocalFilesLoading,
    ]);

    const renderPendingFiles = useCallback(() => {
      return (
        <FCUploadDataGrid
          sectionTitle={t('fileCatalyst.highSpeedTransferProgress')}
          noDataText={t('fileCatalyst.noFilesQueued')}
          dataGridColumnConfig={pendingFileGridConfig}
          fileGridItemArray={pendingFileArray}
        />
      );
    }, [pendingFileArray, pendingFileGridConfig]);

    const renderUploadedFiles = useCallback(() => {
      return (
        <FCUploadDataGrid
          sectionTitle={t('fileCatalyst.uploadedFiles', {
            count: uploadedFileArray?.length,
          })}
          noDataText={t('fileCatalyst.noFilesUploaded')}
          dataGridColumnConfig={uploadedFileGridConfig}
          fileGridItemArray={uploadedFileArray}
        />
      );
    }, [uploadedFileGridConfig, uploadedFileArray]);

    const renderUploadingStatus = useCallback(() => {
      return (
        <Box className={'uploading-status-master-container'}>
          {pendingFileArray?.length > 0 ? (
            <Box className="upload-loading-container" tabIndex={0}>
              <AurLoadingIndicator size={18} className="loader-control" />
              <Typography className="typo-text subhead4_size">
                {t('fileCatalyst.uploading')}
              </Typography>
            </Box>
          ) : null}
          <AurButton
            classNames={'intake-button'}
            type={'continue'}
            label={t('fileCatalyst.initiateIntake')}
            title={t('fileCatalyst.initiateIntake')}
            size={'Large'}
            ariaDescription={t('fileCatalyst.initiateIntake')}
            disableHint={t('fileCatalyst.initiateIntake')}
            disabled={
              pendingFileArray.length > 0 || uploadedFileArray.length === 0
            }
            onClick={handleInitiateUploadClick}
          />
        </Box>
      );
    }, [
      handleInitiateUploadClick,
      pendingFileArray.length,
      uploadedFileArray.length,
    ]);

    const renderRetryConnectionView = useCallback(() => {
      const isFCEncryptedPayloadEmpty = isEmptyInstance(fcEncryptedPayload);
      return (
        <Fragment>
          <Box className={'fc-upload-file-master-container'}>
            <FCRetryConnectionView
              isVisibleFromFCDownloadScreen={isFCEncryptedPayloadEmpty}
              descMessage={
                isFCEncryptedPayloadEmpty
                  ? t('fileCatalyst.highSpeedUploads.fcDownloadRetryDesc')
                  : t('fileCatalyst.highSpeedUploads.fcConnectionRetryDesc')
              }
              onRetry={getHeartBeat}
            />
          </Box>
          {isFCEncryptedPayloadEmpty ? <FileCatalystUnavailableScreen /> : null}
        </Fragment>
      );
    }, [fcEncryptedPayload, getHeartBeat]);

    const renderFCLoaderWithMessage = useCallback(() => {
      return isAgentStartedConnecting ? (
        <Box className={'fc-upload-file-master-container'}>
          <Typography
            className="typo-text body2_size subtitle-text-color"
            tabIndex={0}
          >
            {t('fileCatalyst.highSpeedUploads.fcInitMessage')}
          </Typography>
          <Box className={'fc-loader-container'}>
            <AurLoadingIndicator
              overlay={false}
              isOpen={isAgentStartedConnecting}
            />
          </Box>
        </Box>
      ) : (
        renderRetryConnectionView()
      );
    }, [isAgentStartedConnecting, renderRetryConnectionView]);

    return (
      <Fragment>
        <EvidenceSourceInfo
          showSpeedTest={isAgentRunning || isAgentStartedConnecting}
        />
        {isAgentRunning ? (
          <Fragment>
            <Box className={'fc-upload-file-master-container'}>
              <Box className={'fc-upload-file-container'}>
                {renderLocalFileListContainer()}
                {renderPendingFiles()}
                {renderUploadedFiles()}
                {renderUploadingStatus()}
              </Box>
            </Box>
            {!isEmptyInstance(fileActionModal?.type) && (
              <FileCatalystPopUpModal
                type={fileActionModal?.type}
                isShow={Boolean(fileActionModal?.type)}
                onClose={onCancelDialog}
                onYes={onConfirmYesDialog}
              />
            )}
            {<AurLoadingIndicator overlay={true} isOpen={isIntakeLoading} />}
          </Fragment>
        ) : (
          renderFCLoaderWithMessage()
        )}
      </Fragment>
    );
  }
);

export {UploadHighSpeedTab};
